import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Tabs,
  Tab,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper
} from '@mui/material';
import {
  Print as PrinterIcon,
  Wifi as WifiIcon,
  Usb as UsbIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  PrintDisabled as PrinterDisabledIcon
} from '@mui/icons-material';
import SettingsDialogBase from './SettingsDialogBase';
import DashboardButton from '../../../../common/DashboardButton';

// Mock function for scanning printers
const scanForPrinters = (type) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      if (type === 'wireless') {
        resolve([
          { id: 'printer1', name: 'Epson TM-T88VI', address: '192.168.1.100', status: 'available' },
          { id: 'printer2', name: 'Star mC-Print3', address: '192.168.1.101', status: 'available' }
        ]);
      } else {
        resolve([
          { id: 'usb1', name: 'Epson TM-T20III', port: 'USB001', status: 'available' },
          { id: 'usb2', name: 'Star TSP143IIIU', port: 'USB002', status: 'available' }
        ]);
      }
    }, 1500);
  });
};

const PrinterDialog = ({ open, onClose, currentStatus, onSave }) => {
  const [tabValue, setTabValue] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [manualSettings, setManualSettings] = useState({
    address: '',
    port: '',
    name: '',
  });
  const [testPrinting, setTestPrinting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [receiptSettings, setReceiptSettings] = useState({
    printLogo: true,
    printCustomerCopy: true,
    paperWidth: '80',
    fontSize: 'normal'
  });
  
  // Set initial printer status if available
  useEffect(() => {
    if (currentStatus === 'Connected' && !selectedPrinter) {
      setSelectedPrinter({ id: 'saved', name: 'Saved Printer', status: 'available' });
    }
  }, [currentStatus, selectedPrinter]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleScanPrinters = async () => {
    setScanning(true);
    setAvailablePrinters([]);
    setSelectedPrinter(null);
    
    try {
      const type = tabValue === 0 ? 'wireless' : 'usb';
      const printers = await scanForPrinters(type);
      setAvailablePrinters(printers);
    } catch (error) {
      console.error('Error scanning printers:', error);
    } finally {
      setScanning(false);
    }
  };
  
  const handlePrinterSelect = (printer) => {
    setSelectedPrinter(printer);
  };
  
  const handleManualSettingsChange = (field) => (event) => {
    setManualSettings({
      ...manualSettings,
      [field]: event.target.value
    });
  };
  
  const handleReceiptSettingChange = (setting) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setReceiptSettings({
      ...receiptSettings,
      [setting]: value
    });
  };
  
  const handleTestPrint = async () => {
    setTestPrinting(true);
    setTestResult(null);
    
    // Mock test print
    setTimeout(() => {
      const success = Math.random() > 0.3; // 70% success rate for demo
      setTestResult({
        success,
        message: success ? 'Test print successful!' : 'Failed to connect to printer. Please check connection.'
      });
      setTestPrinting(false);
    }, 2000);
  };
  
  const handleSaveSettings = () => {
    // In a real app, save printer settings to backend
    onSave(selectedPrinter ? 'Connected' : 'Not configured');
  };
  
  // Clear selected printer when switching tabs
  useEffect(() => {
    setSelectedPrinter(null);
    setAvailablePrinters([]);
  }, [tabValue]);
  
  return (
    <SettingsDialogBase
      open={open}
      onClose={onClose}
      title="Printer Configuration"
      description="Configure receipt printers for your POS system"
      onSave={handleSaveSettings}
    >
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <Tab 
              label="Wireless Printer" 
              icon={<WifiIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="USB Printer" 
              icon={<UsbIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>
        
        {/* Wireless Printer Tab */}
        {tabValue === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Available Network Printers
              </Typography>
              <DashboardButton
                startIcon={<RefreshIcon />}
                variant="outlined"
                size="small"
                onClick={handleScanPrinters}
                isLoading={scanning}
              >
                Scan
              </DashboardButton>
            </Box>
            
            {scanning ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : availablePrinters.length > 0 ? (
              <Paper variant="outlined" sx={{ mb: 3 }}>
                <List disablePadding>
                  {availablePrinters.map((printer, index) => (
                    <React.Fragment key={printer.id}>
                      {index > 0 && <Divider />}
                      <ListItem 
                        button
                        selected={selectedPrinter?.id === printer.id}
                        onClick={() => handlePrinterSelect(printer)}
                      >
                        <ListItemIcon>
                          <PrinterIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary={printer.name} 
                          secondary={`IP: ${printer.address}`} 
                        />
                        {selectedPrinter?.id === printer.id && (
                          <CheckIcon color="primary" />
                        )}
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            ) : (
              <Alert 
                severity="info" 
                sx={{ mb: 3 }}
                action={
                  <Button 
                    color="inherit" 
                    size="small"
                    onClick={handleScanPrinters}
                  >
                    SCAN
                  </Button>
                }
              >
                No wireless printers found. Click SCAN to search for available printers.
              </Alert>
            )}
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Manual Configuration
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Printer Name"
                  fullWidth
                  value={manualSettings.name}
                  onChange={handleManualSettingsChange('name')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="IP Address"
                  fullWidth
                  value={manualSettings.address}
                  onChange={handleManualSettingsChange('address')}
                  placeholder="192.168.1.100"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Port"
                  fullWidth
                  value={manualSettings.port}
                  onChange={handleManualSettingsChange('port')}
                  placeholder="9100"
                />
              </Grid>
            </Grid>
          </Box>
        )}
        
        {/* USB Printer Tab */}
        {tabValue === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Available USB Printers
              </Typography>
              <DashboardButton
                startIcon={<RefreshIcon />}
                variant="outlined"
                size="small"
                onClick={handleScanPrinters}
                isLoading={scanning}
              >
                Scan
              </DashboardButton>
            </Box>
            
            {scanning ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : availablePrinters.length > 0 ? (
              <Paper variant="outlined" sx={{ mb: 3 }}>
                <List disablePadding>
                  {availablePrinters.map((printer, index) => (
                    <React.Fragment key={printer.id}>
                      {index > 0 && <Divider />}
                      <ListItem 
                        button
                        selected={selectedPrinter?.id === printer.id}
                        onClick={() => handlePrinterSelect(printer)}
                      >
                        <ListItemIcon>
                          <PrinterIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary={printer.name} 
                          secondary={`Port: ${printer.port}`} 
                        />
                        {selectedPrinter?.id === printer.id && (
                          <CheckIcon color="primary" />
                        )}
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            ) : (
              <Alert 
                severity="info" 
                sx={{ mb: 3 }}
                action={
                  <Button 
                    color="inherit" 
                    size="small"
                    onClick={handleScanPrinters}
                  >
                    SCAN
                  </Button>
                }
              >
                No USB printers found. Click SCAN to search for connected printers.
              </Alert>
            )}
          </Box>
        )}
        
        {selectedPrinter && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Receipt Settings
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={receiptSettings.printLogo} 
                        onChange={handleReceiptSettingChange('printLogo')}
                      />
                    } 
                    label="Print Logo on Receipt" 
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={receiptSettings.printCustomerCopy} 
                        onChange={handleReceiptSettingChange('printCustomerCopy')}
                      />
                    } 
                    label="Print Customer Copy" 
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Paper Width"
                      value={receiptSettings.paperWidth}
                      onChange={handleReceiptSettingChange('paperWidth')}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value="58">58mm</option>
                      <option value="80">80mm</option>
                    </TextField>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      select
                      label="Font Size"
                      value={receiptSettings.fontSize}
                      onChange={handleReceiptSettingChange('fontSize')}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value="small">Small</option>
                      <option value="normal">Normal</option>
                      <option value="large">Large</option>
                    </TextField>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
            
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <DashboardButton
                variant="outlined"
                color="primary"
                startIcon={<PrinterIcon />}
                onClick={handleTestPrint}
                isLoading={testPrinting}
                sx={{ mb: 2 }}
              >
                Test Print
              </DashboardButton>
              
              {testResult && (
                <Alert 
                  severity={testResult.success ? "success" : "error"} 
                  icon={testResult.success ? <CheckIcon /> : <PrinterDisabledIcon />}
                  sx={{ width: '100%' }}
                >
                  {testResult.message}
                </Alert>
              )}
            </Box>
          </>
        )}
      </Box>
    </SettingsDialogBase>
  );
};

export default PrinterDialog;